'use client';

import React from 'react';
import {
  History,
  RefreshCw,
  UserPlus,
  ShieldCheck,
  XCircle,
  FileText,
  Plus,
  Sparkles,
  Trash2,
  ArrowRightLeft,
  Layers,
  Clock,
} from 'lucide-react';

interface AuditLogTabProps {
  selectedWorkspaceId: string;
  setSelectedWorkspaceId: (id: string) => void;
  selectedWorkspaceName: string;
  setSelectedWorkspaceName: (name: string) => void;
  allWorkspacesDropdown: any[];
  auditLogs: any[];
  loadingAuditLogs: boolean;
  fetchAuditLogs: (workspaceId: string) => void;
  handleOpenWorkspaceBoards?: (wsId: string) => void;
  handleOpenBoardCards?: (boardId: string) => void;
  handleOpenCardDetail?: (cardId: string) => void;
  onSelectLog: (log: any) => void;
}

export const AuditLogTab: React.FC<AuditLogTabProps> = ({
  selectedWorkspaceId,
  setSelectedWorkspaceId,
  selectedWorkspaceName,
  setSelectedWorkspaceName,
  allWorkspacesDropdown,
  auditLogs,
  loadingAuditLogs,
  fetchAuditLogs,
  handleOpenWorkspaceBoards,
  handleOpenBoardCards,
  handleOpenCardDetail,
  onSelectLog,
}) => {
  const getIcon = (action: string) => {
    switch (action) {
      case 'USER_REGISTERED':
        return <UserPlus className="w-4 h-4 text-sky-400" />;
      case 'USER_EMAIL_VERIFIED':
      case 'KYC_APPROVED':
        return <ShieldCheck className="w-4 h-4 text-emerald-400" />;
      case 'KYC_REJECTED':
        return <XCircle className="w-4 h-4 text-rose-400" />;
      case 'KYC_SUBMITTED':
        return <FileText className="w-4 h-4 text-amber-400" />;
      case 'WORKSPACE_CREATED':
      case 'BOARD_CREATED':
      case 'LIST_CREATED':
      case 'CARD_CREATED':
        return <Plus className="w-4 h-4 text-emerald-400" />;
      case 'WORKSPACE_UPDATED':
      case 'BOARD_UPDATED':
      case 'LIST_UPDATED':
      case 'CARD_UPDATED':
        return <Sparkles className="w-4 h-4 text-indigo-400" />;
      case 'WORKSPACE_MEMBER_ROLE_UPDATED':
        return <UserPlus className="w-4 h-4 text-purple-400" />;
      case 'WORKSPACE_MEMBER_ADDED':
        return <UserPlus className="w-4 h-4 text-sky-400" />;
      case 'WORKSPACE_MEMBER_REMOVED':
        return <Trash2 className="w-4 h-4 text-pink-400" />;
      case 'CARD_MOVED':
      case 'LIST_REORDERED':
        return <ArrowRightLeft className="w-4 h-4 text-amber-400" />;
      case 'WORKSPACE_DELETED':
      case 'BOARD_DELETED':
      case 'LIST_DELETED':
      case 'CARD_DELETED':
        return <Trash2 className="w-4 h-4 text-rose-400" />;
      default:
        return <Layers className="w-4 h-4 text-slate-400" />;
    }
  };

  const getMsg = (activity: any) => {
    const meta = activity.meta || {};
    switch (activity.action) {
      case 'USER_REGISTERED':
        return `registered a new account as ${meta.userType || 'MEMBER'}`;
      case 'USER_EMAIL_VERIFIED':
        return `successfully verified email address`;
      case 'KYC_SUBMITTED':
        return `submitted organization KYC documents for company "${meta.companyName || ''}"`;
      case 'KYC_APPROVED':
        return `approved organization KYC for company "${meta.companyName || ''}"`;
      case 'KYC_REJECTED':
        return `rejected organization KYC for company "${meta.companyName || ''}"${meta.rejectionReason ? ` (Reason: ${meta.rejectionReason})` : ''}`;
      case 'WORKSPACE_CREATED':
        return `created the workspace`;
      case 'WORKSPACE_UPDATED':
        return `updated workspace settings`;
      case 'WORKSPACE_DELETED':
        return `deleted workspace`;
      case 'WORKSPACE_MEMBER_ADDED':
        return `added member "${meta.email || meta.userId || ''}" with role ${meta.role || 'MEMBER'}`;
      case 'WORKSPACE_MEMBER_ROLE_UPDATED':
        return `updated member "${meta.email || meta.userId || ''}" role to ${meta.role || 'MEMBER'}`;
      case 'WORKSPACE_MEMBER_REMOVED':
        return `removed member "${meta.email || meta.userId || ''}"`;
      case 'BOARD_CREATED':
        return `created board "${meta.title || ''}"`;
      case 'BOARD_UPDATED':
        return `renamed board to "${meta.newTitle || ''}" (was "${meta.oldTitle || ''}")`;
      case 'BOARD_DELETED':
        return `deleted board "${meta.title || ''}"`;
      case 'LIST_CREATED':
        return `added list column "${meta.title || ''}"`;
      case 'LIST_UPDATED':
        return `renamed column "${meta.oldTitle || ''}" to "${meta.newTitle || ''}"`;
      case 'LIST_DELETED':
        return `deleted column "${meta.title || ''}"`;
      case 'LIST_REORDERED':
        return `reordered list column "${meta.title || ''}"`;
      case 'CARD_CREATED':
        return `created task card "${meta.title || ''}"`;
      case 'CARD_UPDATED':
        return `updated card "${meta.newTitle || meta.title || ''}"`;
      case 'CARD_MOVED':
        return `moved card "${meta.title || ''}"`;
      case 'CARD_DELETED':
        return `deleted card "${meta.title || ''}"`;
      default:
        return activity.action.toLowerCase().replace(/_/g, ' ');
    }
  };

  return (
    <div className="bg-[#0A0D1A] border border-slate-800/80 rounded-3xl p-6 sm:p-8 shadow-2xl space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-800/80 pb-6">
        <div>
          <h2 className="text-lg font-black text-white flex items-center gap-2">
            <History className="w-5 h-5 text-indigo-400" />
            <span>Platform Audit Log Explorer</span>
          </h2>
          <p className="text-xs text-slate-400 mt-1">Review granular workspace operations, list mutations, and task movements.</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Select Workspace:</label>
            <select
              value={selectedWorkspaceId}
              onChange={(e) => {
                const targetId = e.target.value;
                if (targetId === '__GLOBAL__') {
                  setSelectedWorkspaceId('__GLOBAL__');
                  setSelectedWorkspaceName('Global Platform Events');
                } else {
                  const matched = allWorkspacesDropdown.find((w) => w.id === targetId);
                  setSelectedWorkspaceId(targetId);
                  if (matched) {
                    setSelectedWorkspaceName(matched.name);
                  }
                }
              }}
              className="bg-[#060811] border border-slate-800 text-indigo-300 font-semibold text-xs rounded-xl px-3 py-2 focus:outline-none focus:border-indigo-500 cursor-pointer min-w-[200px]"
            >
              <option value="__GLOBAL__" className="bg-[#060811] text-indigo-400 font-bold">
                Global Platform Events (All)
              </option>
              {allWorkspacesDropdown.map((w) => (
                <option key={w.id} value={w.id} className="bg-[#060811] text-slate-200">
                  {w.name} (/{w.slug})
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={() => selectedWorkspaceId && fetchAuditLogs(selectedWorkspaceId)}
            disabled={loadingAuditLogs || !selectedWorkspaceId}
            className="p-2.5 rounded-xl bg-[#060811] border border-slate-800 text-slate-300 hover:text-white hover:border-slate-700 transition-all flex items-center gap-1.5 text-xs font-bold disabled:opacity-40 cursor-pointer"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loadingAuditLogs ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {loadingAuditLogs ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <RefreshCw className="w-8 h-8 text-indigo-500 animate-spin" />
          <p className="text-xs text-slate-400 font-medium">Fetching audit entries from PostgreSQL...</p>
        </div>
      ) : auditLogs.length > 0 ? (
        <div className="relative border-l border-slate-800 ml-4 pl-6 space-y-6">
          {auditLogs.map((act) => (
            <div
              key={act.id}
              onClick={() => onSelectLog(act)}
              className="relative group p-3 rounded-2xl bg-[#060811]/60 hover:bg-[#060811] border border-slate-800/50 hover:border-indigo-500/50 transition-all cursor-pointer"
            >
                <span className="absolute -left-[37px] top-4 flex items-center justify-center w-7 h-7 rounded-full bg-slate-950 border border-slate-800 shadow-md group-hover:border-indigo-500 transition-colors">
                  {getIcon(act.action)}
                </span>

                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 space-y-1">
                    <p className="text-xs text-slate-200">
                      <span className="font-bold text-white group-hover:text-indigo-300 transition-colors">
                        {act.actor?.name || 'Administrator'}
                      </span>{' '}
                      {getMsg(act)}
                    </p>

                    <div className="flex flex-wrap items-center gap-2 pt-1">
                      <span className="text-[10px] text-slate-500 flex items-center gap-1 font-mono">
                        <Clock className="w-3 h-3 text-slate-600" /> {new Date(act.createdAt).toLocaleString()}
                      </span>

                      {act.cardId && handleOpenCardDetail && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenCardDetail(act.cardId);
                          }}
                          className="px-2 py-0.5 bg-indigo-500/10 hover:bg-indigo-500/25 border border-indigo-500/30 text-indigo-300 text-[10px] font-bold rounded-md transition-all cursor-pointer"
                        >
                          Inspect Card &rarr;
                        </button>
                      )}

                      {act.boardId && handleOpenBoardCards && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenBoardCards(act.boardId);
                          }}
                          className="px-2 py-0.5 bg-purple-500/10 hover:bg-purple-500/25 border border-purple-500/30 text-purple-300 text-[10px] font-bold rounded-md transition-all cursor-pointer"
                        >
                          Inspect Board &rarr;
                        </button>
                      )}

                      {act.workspaceId && handleOpenWorkspaceBoards && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenWorkspaceBoards(act.workspaceId);
                          }}
                          className="px-2 py-0.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-[10px] font-bold rounded-md transition-all cursor-pointer"
                        >
                          Inspect Workspace &rarr;
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>
      ) : (
        <div className="text-center py-20 bg-[#060811] border border-dashed border-slate-800 rounded-3xl">
          <History className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-xs text-slate-400 font-medium">No actions logged in workspace "{selectedWorkspaceName}" yet</p>
        </div>
      )}
    </div>
  );
};
